import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import { Navigate, useParams } from 'react-router';
import TerminalLayout from '../layout/TerminalLayout';
import TerminalOrangeButton from '../ui/TerminalOrangeButton';
import { useGetDocQuery, useConfirmDocMutation } from '../../store/api/docConfirmationApi';
import { useAuth } from '../../hooks/useAuth';
import routes from '../../routes/routes';
import { formatMoney } from '../../helpers/dataFormatter';
import { getGeoCoords } from '../../helpers/geoCoords';
import { documentCardSx } from '../../theme/terminalStyles';

const Row = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 1, py: 0.75, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
    <Typography color="text.secondary" sx={{ minWidth: 120, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography fontWeight={500} sx={{ minWidth: 0, wordBreak: 'break-word' }}>
      {value ?? '—'}
    </Typography>
  </Box>
);

const DocConfirmationViewPage = () => {
  const { docId } = useParams();
  const { authState } = useAuth();
  const user = authState.user;
  const [askConfirm, setAskConfirm] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { data: doc, isLoading, error } = useGetDocQuery(docId, { skip: !docId });
  const [confirmMutation, { isLoading: isConfirming }] = useConfirmDocMutation();

  if (!user?.terminalWorkplace?.isDocConfirmation) {
    return <Navigate to={routes.root} replace />;
  }

  const handleConfirm = async () => {
    try {
      const coords = await getGeoCoords();
      await confirmMutation({
        docId: Number(docId),
        latitude: coords.latitude,
        longitude: coords.longitude,
      }).unwrap();
      setAskConfirm(false);
    } catch (e) {
      setAskConfirm(false);
      setErrorMessage(e?.data?.message || e?.message || 'Не вдалося підтвердити');
    }
  };

  const routeName = doc?.routeList?.name || doc?.routeList?.number || '—';
  const hasCoords = doc?.latitude != null && doc?.longitude != null;

  return (
    <TerminalLayout
      title="Накладна"
      titleIcon={<AssignmentTurnedInOutlinedIcon />}
      backTo={routes.docConfirmation}
    >
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error || !doc ? (
        <Typography color="error">Документ не знайдено</Typography>
      ) : (
        <>
          <Paper sx={documentCardSx} elevation={0}>
            <Row label="Номер" value={doc.number} />
            <Row label="Дата" value={doc.date ? new Date(doc.date).toLocaleDateString('uk-UA') : '—'} />
            <Row label="Клієнт" value={doc.clientName || '—'} />
            <Row label="Сума" value={`${formatMoney(doc.sum ?? 0)} ₴`} />
            <Row label="Маршрут" value={routeName} />
            {doc.comment ? <Row label="Коментар" value={doc.comment} /> : null}
            <Row label="Статус" value={doc.isConfirmed ? 'Підтверджено' : 'Не підтверджено'} />
            {doc.isConfirmed ? (
              <>
                <Row
                  label="Час (UTC)"
                  value={doc.confirmedAtUtc ? new Date(doc.confirmedAtUtc).toISOString() : '—'}
                />
                <Row label="Час (Київ)" value={doc.confirmedAtKyiv || '—'} />
                <Row
                  label="Координати"
                  value={hasCoords ? `${doc.latitude}, ${doc.longitude}` : 'немає'}
                />
                <Row label="Підтвердив" value={doc.confirmedByUser?.name || '—'} />
              </>
            ) : null}
          </Paper>
          {doc.isConfirmed ? (
            <Typography sx={{ mt: 2, textAlign: 'center', fontWeight: 600 }}>
              Документ вже підтверджено!
            </Typography>
          ) : (
            <TerminalOrangeButton fullWidth sx={{ mt: 2 }} onClick={() => setAskConfirm(true)}>
              Підтвердити документ
            </TerminalOrangeButton>
          )}
        </>
      )}

      <Dialog open={askConfirm} onClose={() => setAskConfirm(false)}>
        <DialogTitle>Підтвердити документ?</DialogTitle>
        <DialogContent>
          <Typography>Номер: {doc?.number}</Typography>
          <Typography>Клієнт: {doc?.clientName || '—'}</Typography>
          <Typography>Сума: {formatMoney(doc?.sum ?? 0)} ₴</Typography>
          <Typography>Маршрут: {routeName}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <TerminalOrangeButton
            onClick={() => setAskConfirm(false)}
            sx={{ bgcolor: 'transparent', color: 'text.primary', boxShadow: 'none' }}
          >
            Ні
          </TerminalOrangeButton>
          <TerminalOrangeButton onClick={handleConfirm} disabled={isConfirming}>
            {isConfirming ? <CircularProgress size={22} color="inherit" /> : 'Так'}
          </TerminalOrangeButton>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(errorMessage)} onClose={() => setErrorMessage('')}>
        <DialogTitle>Повідомлення</DialogTitle>
        <DialogContent>
          <Typography>{errorMessage}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <TerminalOrangeButton onClick={() => setErrorMessage('')}>OK</TerminalOrangeButton>
        </DialogActions>
      </Dialog>
    </TerminalLayout>
  );
};

export default DocConfirmationViewPage;
